import {
  ExpensesCustomizationConfigResourceTypeEnum,
  ExpensesValueObjectsCustomizationDocumentTypeEnum,
} from 'generated/resources/graphql'

import {
  CustomizationField,
  Resources,
  fieldsFor,
} from 'modules/expenses/hooks/useExpensesCustomization'

const mandatoryFields = (fields: CustomizationField[], resourceType: keyof typeof Resources) =>
  fieldsFor(fields, resourceType)
    .filter((field) => {
      return field.mandatory && field.enabled
    })
    .map((field) => ({ id: field.identifier, documentType: field.documentType }))

const enabledFields = (fields: CustomizationField[], resourceType: keyof typeof Resources) =>
  fieldsFor(fields, resourceType)
    .filter((field) => field.enabled)
    .map((field) => ({ id: field.identifier, documentType: field.documentType }))

export const isMandatory = (
  field: string,
  resourceType: keyof typeof Resources,
  fields: CustomizationField[],
  documentType: ExpensesValueObjectsCustomizationDocumentTypeEnum
): boolean => {
  documentType = documentType === 'invoice' ? 'invoice' : 'default'

  const _mandatoryFields = mandatoryFields(fields, resourceType)
  const _specificField = _mandatoryFields.find(
    ({ documentType: type, id }) => type === documentType && id === field
  )

  if (_specificField) {
    return true
  } else {
    return _mandatoryFields
      .filter(({ documentType: type }) => type === 'default')
      .some(({ id }) => id === field)
  }
}

const isEnabled = (
  field: string,
  resourceType: keyof typeof Resources,
  fields: CustomizationField[],
  documentType: ExpensesValueObjectsCustomizationDocumentTypeEnum = 'default'
): boolean => {
  const _enabledFields = enabledFields(fields, resourceType)
  const _specificField = _enabledFields.find(
    ({ id, documentType: type }) => id === field && type === documentType
  )

  if (_specificField) {
    return true
  } else {
    return _enabledFields
      .filter(({ documentType: type }) => type === 'default')
      .some(({ id }) => id === field)
  }
}

export const hasAnyEnabledField = (
  fieldIdentifiers: string[],
  resourceType: keyof typeof Resources,
  fields: CustomizationField[],
  documentType: ExpensesValueObjectsCustomizationDocumentTypeEnum = 'default'
): boolean => {
  return fieldIdentifiers.some((fieldIdentifier) =>
    isEnabled(fieldIdentifier, resourceType, fields, documentType)
  )
}

export interface CustomizationProps {
  customizationFields: CustomizationField[]
  resourceType: ExpensesCustomizationConfigResourceTypeEnum
  documentType: ExpensesValueObjectsCustomizationDocumentTypeEnum
}

export interface WithCustomizationFieldsProps extends CustomizationProps {
  fieldIdentifier: string
  isFieldEnabled?: boolean
  isFieldMandatory?: boolean
}

const withCustomizationFields = <P extends WithCustomizationFieldsProps>(
  WrappedComponent: React.ComponentType<P>
) => {
  const WithCustomizationFields = (props: P) => {
    const { customizationFields, resourceType, documentType, fieldIdentifier } = props

    const isFieldEnabled = isEnabled(
      fieldIdentifier,
      resourceType,
      customizationFields,
      documentType
    )
    const isFieldMandatory = isMandatory(
      fieldIdentifier,
      resourceType,
      customizationFields,
      documentType
    )

    if (!isFieldEnabled) return null

    return (
      <WrappedComponent
        {...props}
        isFieldEnabled={isFieldEnabled}
        isFieldMandatory={isFieldMandatory}
      />
    )
  }

  return WithCustomizationFields
}

export default withCustomizationFields
